import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { quickAnswerErrorMessage, uploadQuickAnswerFile, uploadQuickAnswerImage } from '../../api/quickAnswers'
import QuickAnswerPreview from './QuickAnswerPreview'
import { acceptFor, enableBlocker } from './quickAnswerRules'

const ghost = 'h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 disabled:opacity-50'
const field = 'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-500 disabled:bg-slate-50'

function FieldError({ code }) {
  const { t } = useTranslation()
  return code ? <p role="alert" className="text-xs text-red-600">{t(`quickAnswer.errors.${code}`)}</p> : null
}

export default function QuickAnswerEditor({
  tenantId, draft, isNew, dirty, canWrite, busy, limits, starters, warnings, fieldErrors, canSave,
  onChange, onStarter, onSave, onDelete, onBusy,
}) {
  const { t } = useTranslation()
  const imageInput = useRef(null)
  const fileInput = useRef(null)
  const disabled = !canWrite || busy
  const blocker = enableBlocker(draft.text)
  const atMax = draft.attachments.length >= limits.attachments

  const toggle = (checked) => {
    if (checked && blocker) {
      toast.error(t(`quickAnswer.errors.${blocker}`))
      return
    }
    onChange({ enabled: checked })
  }

  const upload = async (event, kind) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    const maxBytes = kind === 'image' ? limits.image_bytes : limits.file_bytes
    if (maxBytes && file.size > maxBytes) {
      toast.error(t('quickAnswer.attachments.tooLarge', { max: Math.floor(maxBytes / (1024 * 1024)) }))
      return
    }
    onBusy(true)
    try {
      const send = kind === 'image' ? uploadQuickAnswerImage : uploadQuickAnswerFile
      const attachment = await send(tenantId, file)
      onChange({ attachments: [...draft.attachments, attachment] })
    } catch (error) {
      toast.error(quickAnswerErrorMessage(t, error))
    } finally {
      onBusy(false)
    }
  }

  return (
    <section aria-label={draft.title || t('quickAnswer.list.newDraft')} className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
      <div className="flex flex-col gap-4 rounded-lg border border-slate-200 bg-white p-4">
        {isNew && canWrite && (
          <div className="flex flex-col gap-2">
            <p className="text-sm font-semibold text-slate-900">{t('quickAnswer.editor.starters')}</p>
            <p className="text-xs text-slate-500">{t('quickAnswer.editor.startersHint')}</p>
            <div className="flex flex-wrap gap-2">
              {starters.map((starter) => (
                <button key={starter.key} type="button" className={ghost} disabled={busy} onClick={() => onStarter(starter)}>
                  {starter.title}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-col gap-1">
          <label htmlFor="qa-title" className="text-sm font-medium text-slate-700">{t('quickAnswer.editor.titleLabel')}</label>
          <input id="qa-title" value={draft.title} maxLength={limits.title} disabled={disabled} className={field}
            onChange={(event) => onChange({ title: event.target.value })} />
          <span className="text-xs text-slate-400">
            {t('quickAnswer.editor.charCount', { count: draft.title.length, max: limits.title })}
          </span>
        </div>
        <FieldError code={fieldErrors.title} />

        <div className="flex flex-col gap-1">
          <label htmlFor="qa-description" className="text-sm font-medium text-slate-700">{t('quickAnswer.editor.descriptionLabel')}</label>
          <textarea id="qa-description" value={draft.description} maxLength={limits.description_max} rows={3} disabled={disabled}
            className={field} aria-describedby="qa-description-hint"
            onChange={(event) => onChange({ description: event.target.value })} />
          <span id="qa-description-hint" className="text-xs text-slate-500">{t('quickAnswer.editor.descriptionHint')}</span>
          <span className="text-xs text-slate-400">
            {t('quickAnswer.editor.charCount', { count: draft.description.length, max: limits.description_max })}
          </span>
        </div>
        <FieldError code={fieldErrors.description} />

        <div className="flex flex-col gap-1">
          <label htmlFor="qa-text" className="text-sm font-medium text-slate-700">{t('quickAnswer.editor.textLabel')}</label>
          <textarea id="qa-text" value={draft.text} maxLength={limits.text} rows={5} disabled={disabled} className={field}
            onChange={(event) => onChange({ text: event.target.value })} />
          <span className="text-xs text-slate-400">
            {t('quickAnswer.editor.charCount', { count: draft.text.length, max: limits.text })}
          </span>
        </div>
        <FieldError code={fieldErrors.text} />
        {blocker && canWrite && draft.text.trim() !== '' && (
          <p className="text-xs text-amber-700">{t(`quickAnswer.errors.${blocker}`)}</p>
        )}

        <div className="flex flex-col gap-2">
          <p className="text-sm font-medium text-slate-700">{t('quickAnswer.attachments.title')}</p>
          {draft.attachments.map((a, index) => (
            <div key={a.url} className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 p-2 text-sm">
              <span className="break-all">{a.filename || a.url}</span>
              {canWrite && (
                <button type="button" className="text-red-600 underline" disabled={busy}
                  onClick={() => onChange({ attachments: draft.attachments.filter((_, i) => i !== index) })}>
                  {t('quickAnswer.attachments.remove')}
                </button>
              )}
            </div>
          ))}
          {canWrite && (
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" className={ghost} disabled={busy || atMax} onClick={() => imageInput.current?.click()}>
                {t('quickAnswer.attachments.addImage')}
              </button>
              <button type="button" className={ghost} disabled={busy || atMax} onClick={() => fileInput.current?.click()}>
                {t('quickAnswer.attachments.addFile')}
              </button>
              <span className="text-xs text-slate-400">
                {t('quickAnswer.attachments.count', { count: draft.attachments.length, max: limits.attachments })}
              </span>
              <input ref={imageInput} type="file" hidden data-testid="qa-image-input"
                accept={acceptFor(limits.image_extensions) || 'image/jpeg,image/png,image/webp'}
                onChange={(event) => upload(event, 'image')} />
              <input ref={fileInput} type="file" hidden data-testid="qa-file-input"
                accept={acceptFor(limits.file_extensions) || undefined}
                onChange={(event) => upload(event, 'file')} />
            </div>
          )}
          <FieldError code={fieldErrors.attachments} />
        </div>

        {warnings.length > 0 && (
          <ul className="flex flex-col gap-1 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
            aria-label={t('quickAnswer.warningsTitle')}>
            {warnings.map((w) => (
              <li key={w.code}>{t(`quickAnswer.warnings.${w.code}`, { count: w.count, max: w.max })}</li>
            ))}
          </ul>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" role="switch" checked={draft.enabled} disabled={disabled || isNew}
              aria-label={t('quickAnswer.editor.enable')}
              aria-describedby={isNew && canWrite ? 'qa-enable-hint' : undefined} onChange={(event) => toggle(event.target.checked)} />
            {draft.enabled ? t('quickAnswer.editor.on') : t('quickAnswer.editor.off')}
          </label>
          {isNew && canWrite && <span id="qa-enable-hint" className="text-xs text-slate-500">{t('quickAnswer.editor.enableAfterSave')}</span>}
          {canWrite && (
            <div className="ml-auto flex gap-2">
              {!isNew && (
                <button type="button" className="h-9 rounded-lg border border-red-200 bg-white px-3 text-sm font-medium text-red-700 disabled:opacity-50"
                  disabled={busy} onClick={onDelete}>
                  {t('quickAnswer.editor.delete')}
                </button>
              )}
              <button type="button" disabled={busy || !canSave || (!isNew && !dirty)}
                className="h-9 rounded-lg bg-blue-600 px-4 text-sm font-semibold text-white disabled:opacity-50" onClick={onSave}>
                {isNew ? t('quickAnswer.editor.create') : t('quickAnswer.editor.save')}
              </button>
            </div>
          )}
        </div>
      </div>
      <QuickAnswerPreview draft={draft} />
    </section>
  )
}
